import { IconButtonWithTooltip, IconButtonWithTooltipProps } from '@components/utility/buttons';
import Close from '@mui/icons-material/Close';
import { Avatar, AvatarProps, Card, CardContent, CardProps, Chip, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import { cloneElement, useMemo } from 'react';

/**
 * Props for the ActionCard component.
 *
 * @param closeButtonProps - Properties for the close button with tooltip
 * @param typographyElements - Array of Typography React elements to display
 * @param chips - Array of Chip React elements to display
 *
 * The union type ensures mutual exclusivity between avatar and img props:
 * - Provide `avatar` props OR `img` props, but not both
 * - Both can be omitted (neither provided)
 * This prevents conflicts between different display modes for the card's visual element.
 */
export type ActionCardProps = CardProps & {
    /** Properties for the close button with tooltip */
    closeButtonProps?: IconButtonWithTooltipProps;
    /** Array of MUI Typography React elements to display */
    typographyElements: React.ReactElement<typeof Typography>[];
    /** Array of MUI Chip React elements to display */
    chips: React.ReactElement<typeof Chip>[];
    /** Whether the card is in a loading state */
    loading?: boolean;
} & (
        | {
              /** Properties for the avatar.
               *
               * If supplied, `img` must not be provided. */
              avatar: AvatarProps;
              img?: never;
          }
        | {
              /** Properties for the image to display.
               *
               * If supplied, `avatar` must not be provided. */
              img: React.ReactElement;
              avatar?: never;
          }
        | {
              img?: never;
              avatar?: never;
          }
    );

const isImageType = (element: React.ReactElement): element is React.ReactElement<HTMLImageElement> =>
    element.type === 'img';

const ActionCard = ({
    closeButtonProps,
    avatar,
    img,
    typographyElements,
    chips,
    loading = false,
    ...props
}: ActionCardProps) => {
    const AvatarElement = useMemo(
        () => (avatar ? <Avatar variant="square" className="size-24 rounded-lg" {...avatar} /> : null),
        [avatar]
    );

    const ImgElement = useMemo(
        () =>
            img && isImageType(img)
                ? cloneElement(img, {
                      className: clsx('size-28 object-contain', img.props.className),
                      alt: img.props.alt ?? 'Action Card Image',
                  })
                : img,
        [img]
    );

    return (
        <Card {...props} className={clsx('rounded-lg black-border min-w-[233px] w-[233px]', props.className)}>
            <CardContent className="relative flex flex-col items-center py-[22px]">
                {closeButtonProps && !loading && (
                    <IconButtonWithTooltip
                        {...closeButtonProps}
                        className={clsx('absolute right-1 top-1', closeButtonProps.className)}
                        style={{
                            ...closeButtonProps.style,
                            pointerEvents: 'auto',
                        }}
                    >
                        <Close />
                    </IconButtonWithTooltip>
                )}
                {avatar ? loading ? <Skeleton variant="rounded">{AvatarElement}</Skeleton> : AvatarElement : null}
                {img ? loading ? <Skeleton variant="circular">{ImgElement}</Skeleton> : ImgElement : null}
                <div className="flex flex-col mt-2 text-center max-w-full px-2">
                    {loading ? <Skeleton variant="text">{typographyElements}</Skeleton> : typographyElements}
                </div>
                <div className="flex mt-[6px] gap-2 justify-center flex-wrap items-center">
                    {loading ? <Skeleton variant="rectangular">{chips}</Skeleton> : chips}
                </div>
            </CardContent>
        </Card>
    );
};

export { ActionCard };
